import { AxiosResponse, AxiosResponseHeaders } from 'axios';
import { RelativeRoutingType } from 'react-router-dom';

export type Libraries = ('drawing' | 'geometry' | 'localContext' | 'places' | 'visualization')[];
export const MAP_LIBRARIES: Libraries = ['drawing'];
export type ThemeInfo = {
  primaryColor: string;
  logo: string;
};

export enum ScanMode {
  TRANSFORM = 'TRANSFORM',
  SINGLE = 'SINGLE'
}

export interface CustomLocationState {
  prevPage?: string;
}
export interface CustomNavigateOptions {
  replace?: boolean;
  state?: CustomLocationState;
  preventScrollReset?: boolean;
  relative?: RelativeRoutingType;
}
export type pathTo = '/' | '/scanner' | '/admin' | '/admin/organisations' | '/management';
export interface CustomNavigateFunction {
  (to: pathTo, options?: CustomNavigateOptions): void;
  (delta: number): void;
}

export interface Graph {
  edges: [string, string][];
  nodes: { id: string; created: string }[];
}

export type ResponseType<T> = {
  data: T;
  headers?:
    | AxiosResponseHeaders
    | Partial<
        Record<string, string> & {
          'set-cookie'?: string[] | undefined;
        }
      >;
};
export type Location = Record<string, [number, number]>;
export type Locations = {
  reducedLocations: Location;
  allLocations: Location;
};

export enum IDs {
  CU = 'CU',
  C1 = 'C1',
  C2 = 'C2',
  C3 = 'C3',
  C4 = 'C4',
  C5 = 'C5',
  C6 = 'C6',
  C7 = 'C7',
  C8 = 'C8',
  B1 = 'B1',
  B2 = 'B2',
  B3 = 'B3',
  B4 = 'B4',
  B5 = 'B5',
  PO = 'PO',
  PI1 = 'PI1',
  PI2 = 'PI2',
  PI3 = 'PI3',
  P1 = 'P1',
  PP = 'PP',
  MB = 'MB',
  CA = 'CA',
  O = 'O',
  CH = 'CH',
  PA = 'PA',
  E = 'E',
  SL = 'SL',
  PE = 'PE'
}

export const EPCToText: Record<IDs, { what: string; org: string; link?: string }> = {
  B1: { what: 'Nötkött', org: 'Bjursunds Slakteri AB', link: 'http://www.bjursundsslakteri.se/' },
  B2: { what: 'Nötkött', org: 'A J Dahlbergs Slakteri AB', link: 'https://dahlbergskott.se/' },
  B3: { what: 'Nötkött', org: 'Bjursunds Slakteri AB', link: 'http://www.bjursundsslakteri.se/' },
  B4: { what: 'Nötkött', org: 'A J Dahlbergs Slakteri AB', link: 'https://dahlbergskott.se/' },
  B5: { what: 'Nötkött', org: 'Bjursunds Slakteri AB', link: 'http://www.bjursundsslakteri.se/' },
  C1: { what: 'Ko', org: 'Bjursunds Säteri Lantbruks AB' },
  C2: { what: 'Ko', org: 'Murelo AB' },
  C3: { what: 'Ko', org: 'Hökfors Gård' },
  C4: { what: 'Ko', org: 'Norrmem Förvaltning AB' },
  C5: { what: 'Ko', org: 'Årberg Djur AB' },
  C6: { what: 'Ko', org: 'Framnäs Gård' },
  C7: { what: 'Ko', org: 'Karl & Erik Larsson' },
  C8: { what: 'Ko', org: 'Gert Berg' },
  CU: { what: 'Gurka', org: 'Järna Gurkodling AB', link: 'https://jarnagurkan.se/' },
  MB: { what: 'Köttbullar', org: 'Den Svenska Matfabriken AB', link: 'https://svenskamatfabriken.se/' },
  PI1: { what: 'Gris', org: 'Hilmers Lantbruk' },
  PI2: { what: 'Gris', org: 'Hagsgårds Lantbruk' },
  PI3: { what: 'Gris', org: 'Hans Rudell' },
  P1: { what: 'Potatis', org: 'Mårtorps gård' },
  PO: { what: 'Fläskkött', org: 'A J Dahlbergs Slakteri AB', link: 'https://dahlbergskott.se/' },
  PP: { what: 'Potatis', org: 'Skalerian AB', link: 'https://skaleriet.se/index.asp' },
  CA: { what: 'Morot', org: 'Morot' },
  CH: { what: 'Kyckling', org: 'Kyckling' },
  E: { what: 'Ägg', org: 'Hönseri' },
  O: { what: 'Lök', org: 'Lökodlare' },
  PA: { what: 'Packeri', org: 'Packeri' },
  PE: { what: 'Skaleri', org: 'Skaleri' },
  SL: { what: 'Slakteri', org: 'Slakteri' }
};

export const infinitePagination: BaseListParams = { limit: Number.MAX_SAFE_INTEGER, offset: 0 };

export interface TokenReadView {
  token: string;
  refreshToken: string | null; // in case of renewing token with refreshToken, the refreshToken value is null
  sessionId: string;
}

export interface SessionEndView {
  sessionId: string;
}

export interface CustomAxiosResponse extends AxiosResponse {
  data: { message: string; servlet: string; status: number | string; url: string };
}

export interface BaseListView<T> {
  limit: number;
  offset: number;
  totalCount: number;
  records: T[];
}

export interface BaseListParams {
  limit?: number;
  offset?: number;
}
/* ------------------------------------------------------------------ */
/* 1.  Attributes common to *all* organisation DTOs                   */
/* ------------------------------------------------------------------ */
export interface OrganisationBase {
  /** Legal/registered company name */
  name: string;

  /** "Organisations-nummer" / company ID (digits + optional dash) */
  identificationNumber: string;

  /** Country code for organisation number validation (ISO-3166-1 alpha-2) */
  identificationNumberCountry?: string | null;

  /** If articles belonging to this org are hidden */
  disabled?: boolean;

  /** Custom-UI feature flag */
  customUiEnabled?: boolean;

  /** Hex RGB colour used for custom UI (e.g. “#ff5500”) */
  customUiColor?: string | null;

  /** Icon name / URL used for custom UI */
  customUiIcon?: string | null;

  /** Search tags shown in UI – max ≈ 10 words */
  keywords?: string[];

  /** Auto-generated keywords from categories/subcategories (read-only, for UI preview) */
  keywordsAuto?: string[];

  /** If true, keywords are manually entered and not auto-generated */
  keywordsOverride?: boolean;

  /** Should appear on public map / home-screen carousel */
  homeScreenEnabled?: boolean;

  /** VAT / moms number */
  vatNumber?: string | null;

  /** Friendly display name, falls back to `name` when blank */
  displayName?: string | null;

  /** @deprecated Use organisation_map_card.description instead. This field is no longer used in the frontend. */
  description?: string | null;

  /** Long "customer card" enabled? */
  customerCardEnabled?: boolean;
}

/* ------------------------------------------------------------------ */
/* 2.  DTO used when *creating* a row (all required except the truly   */
/*     optional flags)                                                 */
/* ------------------------------------------------------------------ */
export interface OrganisationCreateView extends OrganisationBase {
  /** Overrides inherited optionals that are mandatory on create */
  keywords: string[];
  homeScreenEnabled: boolean;
}

/** 409 body when creating an Organisation whose Org number another Organisation already has. */
export interface OrgNumberTakenError {
  code: 'E0021';
  message: string;
  existingOrganisations: DuplicateOrganisationRef[];
}

export const isOrgNumberTakenError = (body: unknown): body is OrgNumberTakenError =>
  (body as OrgNumberTakenError | undefined)?.code === 'E0021' &&
  Array.isArray((body as OrgNumberTakenError).existingOrganisations);

/* ------------------------------------------------------------------ */
/* 3.  DTO used when *updating* – everything optional + classifierIds  */
/* ------------------------------------------------------------------ */
export interface OrganisationUpdateView extends Partial<OrganisationBase> {
  /** Replace all classifiers with this set (optional) */
  classifierIds?: number[];
  /** If true, keywords are manually entered and should not be auto-generated from categories */
  keywordsOverride?: boolean;
}

/* ------------------------------------------------------------------ */
/* 4.  DTO returned from read/list APIs                               */
/* ------------------------------------------------------------------ */
export interface OrganisationReadView extends OrganisationBase {
  /** Primary key */
  id: number;

  /** Applied category / sub-category IDs (sorted by priority) */
  classifierIds: number[];

  /** Cached number of article templates owned by org */
  articleTemplateCount: number;

  /** Organisation number validation status (read-only) */
  identificationNumberStatus?: 'valid' | 'invalid' | 'unknown' | 'missing' | null;

  /** Normalized organisation number (read-only) */
  identificationNumberNormalized?: string | null;

  /** Organisation number type (read-only) */
  identificationNumberType?: string | null;

  /** Validation reason (read-only) */
  identificationNumberReason?: string | null;

  /**
   * GET /v1/organisations/{id} and list rows: first active address id when server enriches from address list.
   */
  firstActiveAddressId?: number | null;

  /**
   * GET /v1/organisations list rows: mirrors first active address + primary contact when enriched.
   */
  streetAddress?: string | null;
  city?: string | null;
  country?: string | null;
  postalCode?: string | null;
  kommun?: string | null;
  region?: string | null;
  referralLink?: string | null;
  contactName?: string | null;
  contactPhoneNumber?: string | null;
  contactEmail?: string | null;
  contactRole?: string | null;
}
export interface OrganisationListView extends BaseListView<OrganisationReadView> {}

/* ================================================================ *
 *  Organisation-Address DTOs as returned / accepted by the API
 * ================================================================ */

/* ---------------------------------------------------------------- *
 *  Shared properties that can occur on *every* address DTO
 * ---------------------------------------------------------------- */
interface OrganisationAddressBase {
  /** WGS-84 decimal degrees (nullable) */
  lat?: number | null;
  lng?: number | null;

  /** Free-text address block (all nullable) */
  city?: string | null;
  country?: string | null;
  postalCode?: string | null;
  streetAddress?: string | null;
  kommun?: string | null;
  region?: string | null;
  addressName?: string | null;

  /** “VISITING”, “INVOICE” … */
  type?: OrganisationAddressType | null;
  /** Marks the active address for the organisation */
  active?: boolean;

  /** Contacts attached to this address (id refs) */
  contactIds?: number[];
  referralLinkIds?: number[];
}

/* ---------------------------------------------------------------- *
 *  DTO returned from GET calls
 * ---------------------------------------------------------------- */
export interface OrganisationAddressReadView extends OrganisationAddressBase {
  /** Primary key of the address row */
  id: number;
  /** Owning organisation */
  organisationId: number;
}

export interface OrganisationAddressReadViewForMap extends OrganisationAddressReadView {
  organisationName: string;
  organisationDisplayName: string | null;
  organisationKeywords: string[];
  organisationVatNumber: string | null;
  organisationIdentificationNumber: string | null;
}

/* ---------------------------------------------------------------- *
 *  DTO sent when *creating* an address
 *  – all properties optional; defaults handled server-side
 * ---------------------------------------------------------------- */
export interface OrganisationAddressCreateView extends OrganisationAddressBase {
  /** If omitted, server defaults to `"VISITING"` */
  type?: OrganisationAddressType | null;
  /** If omitted, server defaults to `true` */
  active?: boolean;
}

/* ---------------------------------------------------------------- *
 *  DTO sent when *updating* an address
 *  – identical fields, but *all* optional
 * ---------------------------------------------------------------- */
export type OrganisationAddressUpdateView = Partial<OrganisationAddressCreateView>;

/* ================================================================ *
 *  Organisation-Contact DTOs (TypeScript)
 * ================================================================ */

/* ---------------------------------------------------------------- *
 *  Fields shared by every contact DTO
 * ---------------------------------------------------------------- */
interface OrganisationContactBase {
  /** Full name of the person (e.g. “Sara Svensson”) */
  contactName?: string;

  /** E-mail address (nullable) */
  contactEmail?: string | null;

  /** Phone number in E.164 or local format (nullable) */
  contactPhoneNumber?: string | null;
}

/* ---------------------------------------------------------------- *
 *  DTO returned from GET calls
 * ---------------------------------------------------------------- */
export interface OrganisationContactReadView extends OrganisationContactBase {
  /** Primary key of contact row */
  id: number;
  /** Owning organisation */
  organisationId: number;
  /** Name is always present when reading */
  contactName: string;
}

/* ---------------------------------------------------------------- *
 *  DTO sent when creating a contact
 *  – name required, others optional
 * ---------------------------------------------------------------- */
export interface OrganisationContactCreateView extends OrganisationContactBase {
  /** Required when creating */
  contactName: string;
}

/* ---------------------------------------------------------------- *
 *  DTO sent when updating a contact
 *  – every field optional; patch the ones you need
 * ---------------------------------------------------------------- */
export type OrganisationContactUpdateView = Partial<OrganisationContactCreateView>;

export interface OrganisationSummaryAddress {
  id: number;
  lat: number | null;
  lng: number | null;
}

export interface OrganisationSummaryView {
  id: number;
  name: string;
  displayName: string | null;
  disabled: boolean;
  icon: string | null;
  keywords: string[];
  classifierIds: number[];
  customerCardEnabled: boolean;
  vatNumber: string | null;
  identificationNumber: string | null;
  addresses: OrganisationSummaryAddress[];
}

/**
 * GET /v1/group/{groupId}/org-summaries only — group map + GroupMembershipsDialog join.
 * Omits VAT and organisation number (use authenticated org summary for those).
 */
export interface GroupOrgMapSummaryItem {
  id: number;
  name: string;
  displayName: string | null;
  disabled: boolean;
  icon: string | null;
  keywords: string[];
  classifierIds: number[];
  customerCardEnabled: boolean;
  addresses: OrganisationSummaryAddress[];
}

/** GET /v1/organisations-summary list row only (slim; group / single-summary use {@link OrganisationSummaryView}). */
export interface OrganisationHomeSummaryListItem {
  id: number;
  name: string;
  disabled: boolean;
  icon: string | null;
  keywords: string[];
  classifierIds: number[];
  customerCardEnabled: boolean;
  addresses: OrganisationSummaryAddress[];
}

export interface OrganisationHomeSummaryListView extends BaseListView<OrganisationHomeSummaryListItem> {}

/** Rows that share the fields needed to explode orgs into per-address map markers. */
export type OrganisationMapExplodeSource =
  | GroupOrgMapSummaryItem
  | Pick<
      OrganisationSummaryView,
      'id' | 'name' | 'addresses' | 'disabled' | 'icon' | 'keywords' | 'classifierIds' | 'customerCardEnabled'
    >;

export interface OrganisationBasicInfoView {
  id: number;
  name: string;
  customUiIcon: string | null;
  customUiEnabled: boolean;
  customUiColor: string;
}

export interface OrganisationSummaryViewAlt {
  organisationId: number;
  addressId: number;
  name: string;
  lat: number;
  lng: number;
  disabled: boolean;
  icon: string | null;
  keywords: string[];
  classifierIds: number[];
  customerCardEnabled: boolean;
}

export interface OrganisationSummaryListViewAlt extends BaseListView<OrganisationSummaryViewAlt> {}

//_________ Category types _____________
export interface ClassifierCreateView {
  name: string;
  icon: string;
  type: 'CATEGORY' | 'SUB_CATEGORY' | 'GROUP';
  priority: number;
  color: string;
}

export interface ClassifierReadView extends ClassifierCreateView {
  id: number;
  verifiedIcon?: string;
}

export interface ClassifierUpdateView extends ClassifierCreateView {}

export interface ClassifierListView extends BaseListView<ClassifierReadView> {}

//_________ Users types _____________
/** Where the user is in the interface walkthrough; COMPLETED means it isn't shown. */
export type UserOnboardingStatus = 'INTRO' | 'ADDRESSES' | 'CATEGORIES' | 'CUSTOMISATION' | 'MAPS' | 'COMPLETED';

export interface UserReadView {
  id: number;
  email: string;
  isSuperUser: boolean;
  onboardingStatus: UserOnboardingStatus;
}

export interface UserCreateView {
  email: string;
  password: string;
}

export interface UserListView extends BaseListView<UserReadView> {}

// _____ ARTICLE TEMPLATES ________

export interface ArticleTemplateUpdateView {
  templateName: string;
  displayName: string;
  mapIcon: string;
  menuIcon: string;
  organisationId: number;
  referralLink: string;
}

export interface ArticleTemplateCreateView extends ArticleTemplateUpdateView {
  sourceTemplates: number[]; // array of other templates IDs
}

export interface ArticleTemplateReadView extends ArticleTemplateCreateView {
  id: number;
  organisationName: string;
  lat: number;
  lng: number;
}

export interface ArticleTemplateListView extends BaseListView<ArticleTemplateReadView> {}

//_____OrganisationMembership_____
export enum MembershipRole {
  ADMIN = 'ADMIN',
  USER = 'USER'
}
export interface OrganisationMembershipCreateView {
  role: MembershipRole;
}

export interface OrganisationMembershipReadView {
  organisationId: string | number;
  userId: string | number;
  role: MembershipRole;
}

export interface ArticleCreateWithTemplateView {
  alternateId?: string;
  sourceArticles: number[];
}

export interface ArticleReadView {
  alternateIds: string[];
  displayName: string;
  id: number;
  lat: number;
  lng: number;
  mapIcon: string;
  menuIcon: string;
  organisationId: number;
  referralLink: string;
  sourceArticleIds: number[];
  organisationName: string;
  disabled: boolean;
  customUiColor: string | null;
  customIcon: string | null;
  urlAlias: string | null;
  customerCardEnabled: boolean;
}

export interface ArticleCreateView {
  alternateId: string;
  displayName: string;
  organisationId: number;
  referralLink: string;
}

export interface ArticleListView extends BaseListView<ArticleReadView> {}

export interface ArticleGraphView {
  id: number;
  articles: ArticleReadView[];
}

export interface UrlReadView extends UrlCreateView {
  id: number;
  articleIds: number[];
}

export interface UrlListView extends BaseListView<UrlReadView> {}

export interface UrlCreateView {
  articleId?: number;
  articleIds: number[];
  url: string;
  epc: string;
  alternateId: string | null;
  externalLinks: ExternalLink[];
}

export interface UrlUpdateView extends UrlCreateView {}

/** Exactly one of `groupId` or `url` must be populated. */
export interface LinkCreateView {
  name: string;
  groupId?: number | null;
  url?: string | null;
}

/** Exactly one of `groupId` or `url` must be populated. */
export interface LinkRetargetView {
  groupId?: number | null;
  url?: string | null;
}

export interface LinkDisabledView {
  disabled: boolean;
}

/** Exactly one of `groupId` or `url` is present for each scan aggregate. */
export interface ScanCountView {
  groupId?: number | null;
  url?: string | null;
  count: number;
}

export interface LinkReadView {
  id: number;
  organisationId: number;
  name: string;
  groupId?: number | null;
  url?: string | null;
  publicPath: string;
  disabled: boolean;
  scans: ScanCountView[];
}

export interface LinkPublicView extends Omit<LinkReadView, 'scans'> {
  group?: GroupSummaryView;
}

export interface LinkDestinationView {
  id: number;
  name: string;
  member: boolean;
}

export interface ExternalLink {
  externalUrl: string;
  displayText: string;
}

export interface ImportResponseView {
  added: number;
  updated: number;
  deleted: number;
  skipped: number;
  failedRows: string[][];
  skippedRows: string[][];
}

export interface AlternateIdReadView {
  alternateId: string;
  entityType: 'Article' | 'Custom URL';
  entityId: number;
}

export interface GroupCreateView {
  name: string;
  shortName: string;
  icon: string;
  logo: string;
  themeColor: string;
  searchControlEnabled?: boolean;
}

export interface GroupReadView extends GroupCreateView {
  id: number;
  attachedButtonIds?: number[];
}

export interface GroupUpdateView extends GroupCreateView {}

export interface GroupSummaryView {
  id: number;
  name: string;
  shortName: string;
  icon: string;
  themeColor: string;
  logo: string;
  searchControlEnabled?: boolean;
}

export interface GroupListView extends BaseListView<GroupReadView> {}

export interface GroupMembershipUpdateView extends GroupMembershipCreateView {}

export interface GroupAddressMembershipItem {
  organisationAddressId: number;
  icon?: string | null; // optional per-address icon (future-proof)
}

/** GET /v1/group/{groupId}/memberships only — no groupId or org-level membership icon. */
export interface GroupMembershipListItem {
  id: number;
  organisationId: number;
  addressMemberships: GroupAddressMembershipItem[];
}

export interface GroupMembershipReadView {
  id: number;
  icon?: string | null; // org-level icon inside the group
  groupId: number;
  organisationId: number;
  addressMemberships: GroupAddressMembershipItem[]; // <— NEW
  providerId?: number | null;
}

export interface GroupMembershipCreateView {
  icon?: string | null; // org-level icon
  addressMemberships?: GroupAddressMembershipItem[]; // <— NEW
  providerId?: number | null;
}

export interface GroupMembershipUpdateView extends GroupMembershipCreateView {}

//_____Provider_____
export interface ProviderCreateView {
  name: string;
}

export interface ProviderReadView extends ProviderCreateView {
  id: number;
  createdAt: string;
}

export interface ProviderListView extends BaseListView<ProviderReadView> {}

//_____Group bulk-import (Organisations + Addresses + Providers)_____
export interface GroupBulkImportRow {
  name: string;
  displayName?: string | null;
  identificationNumber: string;
  gln?: string | null;
  /** Full address as one string. Takes precedence over streetAddress/houseNumber if both are sent. */
  address?: string;
  streetAddress?: string;
  houseNumber?: string;
  postalCode: string;
  city: string;
  country: string;
  kommun?: string | null;
  region?: string | null;
  /** Required only when the row creates a new address; a matched address keeps its own. */
  lat?: number | null;
  lng?: number | null;
  googlePlaceId?: string | null;
  providerName?: string | null;
}

export type GroupBulkImportIssueReason =
  | 'missingRequiredField'
  | 'invalidCountry'
  | 'invalidOrgNumber'
  | 'conflictNameAndNumberMismatch'
  | 'conflictNameMatchesOtherNumber'
  | 'conflictNumberMatchesOtherName'
  | 'conflictDisplayNameMismatch'
  | 'conflictNumberDifferentNameInBatch'
  | 'missingCoordinates';

export interface GroupBulkImportIssue {
  rowIndex: number;
  reason: GroupBulkImportIssueReason;
  details: Record<string, unknown>;
}

export interface GroupBulkImportRejectedResponse {
  rejected: true;
  issues: GroupBulkImportIssue[];
}

export interface GroupBulkImportResultRow {
  rowIndex: number;
  organisationOutcome: 'created' | 'matched';
  organisationId: number;
  addressId: number;
  providerId: number | null;
  membershipOutcome: 'created' | 'updated';
}

export interface GroupBulkImportSummary {
  organisationsCreated: number;
  organisationsMatched: number;
  providersCreated: number;
  membershipsCreated: number;
  membershipsUpdated: number;
}

export interface GroupBulkImportAcceptedResponse {
  rejected: false;
  results: GroupBulkImportResultRow[];
  summary: GroupBulkImportSummary;
}

export type GroupBulkImportResponse = GroupBulkImportRejectedResponse | GroupBulkImportAcceptedResponse;

export type GroupBulkImportNewInfoReason = 'newOrganisation' | 'newAddress' | 'newGooglePlaceId' | 'newProvider';

/** Same shape as GroupBulkImportIssue, but describes what the import will *create* rather than a
 * problem to fix — only present for rows that have no issues. */
export interface GroupBulkImportNewInfoEntry {
  rowIndex: number;
  reason: GroupBulkImportNewInfoReason;
  details: Record<string, unknown>;
}

/** Response from the validate-import pre-flight check — never writes anything, just reports issues. */
export interface GroupBulkImportValidationResponse {
  valid: boolean;
  issues: GroupBulkImportIssue[];
  /** Everything the import will create if it proceeds — new organisations/addresses/providers. */
  newInformation: GroupBulkImportNewInfoEntry[];
  /** Static Markdown describing every validation/conflict rule the backend applies. Always present,
   * regardless of `valid`. Not returned by the real bulk-import endpoint. */
  validationRules: string;
}

// Organisation address (minimal fields we use in the picker)
export interface OrganisationAddressReadView {
  id: number;
  organisationId: number;
  name?: string | null; // if you have a display name
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  active?: boolean;
}

export interface OrganisationDescriptionCardCreateView {
  displayName?: string;
  description?: string;
  buttonText?: string;
  link1?: string;
  link2?: string;
  link3?: string;
  link3Text?: string;
  bigButtonLinks?: string[];
  organisationId: number;
}

export interface OrganisationDescriptionCardReadView extends OrganisationDescriptionCardCreateView {
  id: number;
}

export interface OrganisationDescriptionCardUpdateView extends OrganisationDescriptionCardCreateView {}

/**
 * Anonymous `GET /v1/organisations/public` — slim catalogue row (not the full public read model).
 */
export interface OrganisationPublicListItemView {
  id: number;
  name: string;
  displayName: string | null;
  lat: number | null;
  lng: number | null;
  customUiEnabled: boolean;
  customUiColor: string | null;
  customUiIcon: string | null;
  disabled: boolean;
  homeScreenEnabled: boolean;
  keywords: string[];
  classifierIds: number[];
}

/** Narrow org fields for `MapCardContent` + MapInfoBox accent. */
export interface MapCardOrgProfile {
  name: string;
  displayName: string | null;
  keywords: string[];
  keywordsAuto?: string[];
  vatNumber: string | null;
  identificationNumber: string;
  customUiIcon: string | null;
  customUiColor: string | null;
  referralLink: string | null;
  /** When set (e.g. admin preview from authenticated read), passed to MapCardContent as addressId */
  firstActiveAddressId?: number | null;
}

/**
 * GET /v1/organisations/public/{id} — slim anonymous org row for map card accent and default referral.
 */
export interface OrganisationPublicReadView {
  id: number;
  name: string;
  displayName: string | null;
  identificationNumber: string;
  vatNumber: string | null;
  customUiIcon: string | null;
  customUiColor: string | null;
  keywords: string[];
  keywordsAuto?: string[];
  referralLink: string | null;
}

/* ---------- Referral Links ---------- */
export interface OrganisationReferralLinkReadView {
  id: number;
  organisationId: number;
  url: string;
}

export interface OrganisationReferralLinkCreateView {
  url: string;
}

export interface OrganisationReferralLinkUpdateView {
  url: string;
}

/** Canonical address types used across the app */
export enum OrganisationAddressType {
  VISITING = 'VISITING',
  HEADQUARTERS = 'HEADQUARTERS',
  REGISTERED = 'REGISTERED',
  BILLING = 'BILLING',
  SHIPPING = 'SHIPPING',
  RETURNS = 'RETURNS',
  WAREHOUSE = 'WAREHOUSE'
}

export enum OrgCardBtnLinkCategory {
  PRIVATE = 'Privatperson',
  COMPANY = 'Företag'
}
export interface OrganisationMapCardButtonReadView {
  id: number; // local uuid
  mapCardId: number;
  icon: string; // base64string
  title: string;
  subtitle: string;
  sortOrder: number;
  referralLinks: OrganisationMapCardButtonLinkReadView[];
}

export interface OrganisationMapCardButtonLinkReadView {
  id: number;
  displayName: string;
  linkId: number;
  category: OrgCardBtnLinkCategory;
  sortOrder: number;
}
export interface OrganisationMapCardButtonCreateView {
  id?: number; // null for new items, present for updates
  mapCardId?: number; // ignored on write
  icon: string; // base64string
  title: string;
  subtitle: string;
  sortOrder?: number; // optional, defaults to array index if not provided
  referralLinks: OrganisationMapCardButtonLinkCreateView[];
}

export interface OrganisationMapCardButtonLinkCreateView {
  id?: number; // null for new items, present for updates
  displayName: string;
  linkId: number;
  category: OrgCardBtnLinkCategory;
  sortOrder?: number; // optional, defaults to array index if not provided
}
export interface OrganisationMapCardReadView {
  id: number;
  organisationId: number;
  displayName: string;
  showIcon: boolean;
  showKeywords: boolean;
  description: string;
  buttons: OrganisationMapCardButtonReadView[];
  orgRefLinkText?: string;
  orgCustomUiIcon?: string;
  imageUrl?: string;
}

/**
 * Anonymous `GET org-map-card/public/by-organisation/{orgId}` — slim payload for public map info box
 * (no card id, no orgCustomUiIcon on card, no button ids/mapCardId/sortOrder, no link sortOrder).
 */
export interface OrganisationMapCardPublicLinkReadView {
  id: number;
  linkId: number;
  displayName?: string | null;
  category: string;
}

export interface OrganisationMapCardPublicButtonReadView {
  icon?: string | null;
  title: string;
  subtitle?: string | null;
  referralLinks: OrganisationMapCardPublicLinkReadView[];
}

export interface OrganisationMapCardPublicReadView {
  organisationId: number;
  displayName?: string | null;
  showIcon: boolean;
  showKeywords: boolean;
  description?: string | null;
  orgRefLinkText?: string | null;
  imageUrl?: string | null;
  buttons: OrganisationMapCardPublicButtonReadView[];
}

export interface OrganisationMapCardCreateView {
  organisationId: number;
  displayName: string;
  showIcon: boolean;
  showKeywords: boolean;
  description: string;
  buttons: OrganisationMapCardButtonCreateView[];
  orgRefLinkText?: string;
  orgCustomUiIcon?: string;
  imageUrl?: string;
}

/** ---- Images API shapes (S3-backed) ---- **/
export interface ImageUploadResponse {
  key: string; // S3 key (users/{userId}/org-map-card/YYYY/MM/DD/uuid.ext)
  url: string; // public URL to use in cards
  sizeBytes: number;
  contentType: string;
}

export interface ImageListItem {
  key: string;
  url: string;
  sizeBytes: number;
  lastModified: string; // ISO string
}

export interface ImageListResponse {
  items: ImageListItem[];
  nextCursor?: string | null;
}

/**
 * Public read model for anonymous `GET org-map-card/button/{buttonId}` and
 * `GET org-map-card/buttons` (no button-level `sortOrder`; batch order follows `ids`).
 */
export interface OrgMapCardButtonReferralLinkPublicReadView {
  id: number;
  linkId: number;
  displayName?: string | null;
  category: string;
  /** Present when `includeUrls=true`; otherwise null or omitted */
  url?: string | null;
}

export interface OrgMapCardButtonPublicReadView {
  id: number;
  icon?: string | null;
  title: string;
  subtitle?: string | null;
  referralLinks: OrgMapCardButtonReferralLinkPublicReadView[];
}

export type ButtonsOverview = {
  items: {
    organisationId: number;
    organisationName: string; // fallback already applied server-side
    cardId: number;
    displayName: string | null;
    cardImageLink?: string | null;
    buttons: Array<{
      id: number;
      icon?: string | null;
      title: string;
      subtitle?: string | null;
      sortOrder: number;
      referralLinks: Array<{
        id: number;
        linkId: number;
        displayName?: string | null;
        category: string;
        url?: string; // present when includeUrls=true
      }>;
    }>;
  }[];
  total: number;
};
/**
 * Organisation number validation result from API
 */
export interface OrgNoResult {
  status: 'valid' | 'invalid' | 'unknown' | 'missing';
  normalized: string | null;
  type: string | null;
  reason: string | null;
}

/**
 * Supported country codes for organisation number validation
 */
export const SUPPORTED_COUNTRIES = [
  { code: 'SE', name: 'Sweden' },
  { code: 'DK', name: 'Denmark' },
  { code: 'NO', name: 'Norway' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' }
] as const;

export type SupportedCountryCode = (typeof SUPPORTED_COUNTRIES)[number]['code'];

// ─── Fortnox ──────────────────────────────────────────────────────────────────

export interface FortnoxGroupRef {
  id: number;
  name: string;
}

/**
 * NONE: every invoicing customer. EXCLUSION: drop customers who only bought listed articles. PREFIX: keep customers
 * who bought an article starting with a prefix, reading every invoice. LOOKUP_PREFIX: the same customers, with
 * Fortnox's invoice search finding the invoices to read. LOOKUP_SUPPLIER: keep customers who bought one of a
 * supplier's articles; needs the connection to be granted the article and supplier scopes.
 */
export type FortnoxArticleFilterMode = 'NONE' | 'EXCLUSION' | 'PREFIX' | 'LOOKUP_PREFIX' | 'LOOKUP_SUPPLIER';

/** What a connection must have been granted for LOOKUP_SUPPLIER. */
export const FORTNOX_SUPPLIER_LOOKUP_SCOPES = ['article', 'supplier'];

/** A supplier in a connection's Fortnox. A Feed keeps the name it had when picked, which may be missing. */
export interface FortnoxSupplier {
  number: string;
  name: string | null;
}

/**
 * Which rows on its Group a Feed's sync removes when no Feed found them: those its connection wrote (`connection`),
 * also its Provider's rows no other Fortnox connection is linked to (`provider`), every row (`group`, the Group's
 * primary Feed only), or none (`none`; the primary's `group` still removes the Feed's rows no Feed found).
 */
export type FortnoxClearScope = 'connection' | 'provider' | 'group' | 'none';

/** One Fortnox connection feeding one Group: the Feed's own settings, and its connection's label and Provider. */
export interface FortnoxFeedView {
  /** The Feed's id. */
  id: number;
  /** The connection's id. */
  credentialId: number;
  groupId: number;
  label: string;
  /** Primary on this Group only. */
  isPrimary: boolean;
  priority: number;
  syncEnabled: boolean;
  invoiceLookbackDays: number;
  invoiceMinAmount: number;
  /** Which filter picks the customers; the other mode's values are kept but ignored. */
  articleFilterMode: FortnoxArticleFilterMode;
  /** As typed, so ranges such as 9000-9004 read back the way they were entered. */
  excludedArticleNumbers: string[] | null;
  articlePrefixes: string[] | null;
  /** The suppliers lookup by supplier uses, in the connection's Fortnox. */
  lookupSuppliers: FortnoxSupplier[] | null;
  /** The connection's OAuth scopes; lookup by supplier needs FORTNOX_SUPPLIER_LOOKUP_SCOPES. */
  grantedScopes: string[];
  fortnoxCompany: string | null;
  connectedAt: string;
  lastSyncedAt: string | null;
  /** The business party this connection's data belongs to, e.g. "Dellback". Null until one is chosen. */
  providerId: number | null;
  providerName: string | null;
  /** Rows the connection synced, on every Group, that belong to its current Provider (or to none): what moves on a change. */
  providerRowCount: number;
  /** Which rows on the Group that no Feed found the sync removes. */
  clearScope: FortnoxClearScope;
  /** Rows on the Group that belong to another Provider or to none: what the `group` scope could remove. */
  otherProvidersRowCount: number;
  /** Who the distributor is mailed at when the connection is added to or removed from a Group. */
  contactEmail: string | null;
  /** The other Groups the connection feeds. */
  otherGroups: FortnoxGroupRef[];
  /** The admin who made the connect link; prefills a missing contact email. */
  connectedByEmail: string | null;
}

/** A Feed's settings. On a change, a missing setting is left as it is. */
export interface FortnoxFeedSettings {
  isPrimary?: boolean;
  priority?: number;
  syncEnabled?: boolean;
  invoiceLookbackDays?: number;
  invoiceMinAmount?: number;
  /** The chosen mode needs at least one value. */
  articleFilterMode?: FortnoxArticleFilterMode;
  excludedArticleNumbers?: string[];
  /** No prefix may be blank or contain a comma. */
  articlePrefixes?: string[];
  /** At least one in LOOKUP_SUPPLIER mode; none blank or chosen twice. */
  lookupSuppliers?: FortnoxSupplier[];
  /** `group` only on the Group's primary Feed. */
  clearScope?: FortnoxClearScope;
}

export interface FortnoxFeedAttach extends FortnoxFeedSettings {
  credentialId: number;
  groupId: number;
}

/** A Group a connection feeds; `id` is the Feed's. */
export interface FortnoxConnectionFeedView {
  id: number;
  groupId: number;
  groupName: string | null;
  isPrimary: boolean;
  syncEnabled: boolean;
}

/** A Fortnox connection: one distributor's Fortnox account, approved once, and the Groups it feeds. */
export interface FortnoxConnectionView {
  id: number;
  label: string;
  providerId: number | null;
  providerName: string | null;
  fortnoxCompany: string | null;
  connectedAt: string;
  contactEmail: string | null;
  providerRowCount: number;
  feeds: FortnoxConnectionFeedView[];
  /** The admin who made the connect link; prefills a missing contact email. */
  connectedByEmail: string | null;
  grantedScopes: string[];
}

export interface FortnoxConnectionUpdateView extends FortnoxConnectionView {
  /** Rows that moved to the new Provider. */
  movedRows: number;
}

export interface FortnoxConnectionPatch {
  label?: string;
  /** Sets or changes the Provider; it can't be cleared. */
  providerId?: number;
  /** Empty clears it. */
  contactEmail?: string;
}

export type FortnoxFeedAction = 'attached' | 'detached' | 'filter_changed';

/** Who attached, detached or changed the filter of a Feed. `details` is the Feed's filter and clearing scope then. */
export interface FortnoxFeedLogEntry {
  id: number;
  credentialId: number;
  groupId: number;
  groupName: string | null;
  action: FortnoxFeedAction;
  userId: number | null;
  userEmail: string | null;
  at: string;
  details: {
    invoiceLookbackDays?: number;
    invoiceMinAmount?: number;
    articleFilterMode?: FortnoxArticleFilterMode;
    excludedArticleNumbers?: string[];
    articlePrefixes?: string[];
    lookupSuppliers?: FortnoxSupplier[];
    /** Entries logged before several suppliers: the one supplier. */
    lookupSupplierNumber?: string | null;
    lookupSupplierName?: string | null;
    /** Entries logged before filter modes: a prefix, when set, won over the exclusion list. */
    supplierArticlePrefix?: string | null;
    /** Missing on entries logged before clearing scopes. */
    clearScope?: FortnoxClearScope;
  } | null;
  /** When the contact was mailed about this attach or detach. */
  notifiedAt: string | null;
  /** Why the contact wasn't mailed: no contact email, or the send failed. */
  notifyError: string | null;
}

export type FortnoxSyncState = 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface FortnoxAddedOrgEntry {
  orgId: number;
  orgName: string;
  customerNumber: string;
}

export interface FortnoxSyncStatus {
  jobId: string;
  groupId: number;
  state: FortnoxSyncState;
  startedAt: string;
  completedAt: string | null;
  /** What the sync did to each address it found: added, claimed (had no Provider), taken over by the primary,
   *  already present under another Provider, or unchanged. */
  added: number;
  claimed: number;
  takenOver: number;
  alreadyPresent: number;
  unchanged: number;
  /** Addresses removed because no Feed found them. */
  removed: number;
  skipped: number;
  failed: number;
  errorMessage: string | null;
  addedOrgs?: FortnoxAddedOrgEntry[];
  /** The connections, by label, whose Feed removed nothing because a customer couldn't be read from Fortnox. */
  removalHeld?: string[];
  /** How many customers couldn't be read from Fortnox. */
  unreadableCustomers?: number;
  /** Whether that stopped clearing the whole Group, so nothing at all was removed. */
  groupRemovalHeld?: boolean;
}

export interface FortnoxSkipLogEntry {
  fortnoxCustomerNumber: string;
  customerName: string;
  skipReason: string;
  sourceCredentialId: number;
  sourceCredentialLabel: string;
}

// ---- DevTool: Duplicate candidates ----

export interface DuplicateOrganisationRef {
  id: number;
  name: string;
}

export interface DuplicateDismissal {
  id: number;
  /** Null when the user who dismissed it was deleted. */
  dismissedBy: { id: number; email: string } | null;
  dismissedAt: string;
  organisationIds: number[];
}

/** Whether a candidate is dismissed as "not a duplicate", and which Organisations are new since the last dismissal. */
export interface DuplicateDismissalState {
  /** The dismissal in effect, or null when the candidate is not dismissed. */
  dismissal: DuplicateDismissal | null;
  /** Organisations missing from the last dismissed set; empty when never dismissed or dismissed now. */
  newOrganisationIds: number[];
}

export interface DuplicateCandidate extends DuplicateDismissalState {
  /** Opaque key; pass it back unchanged to read the candidate. */
  candidateKey: string;
  country: string | null;
  digits: string;
  organisationCount: number;
  organisations: DuplicateOrganisationRef[];
}

export interface DuplicateCandidateList {
  summary: {
    orgNumbersWithDuplicates: number;
    organisationsInvolved: number;
    /** Dismissed candidates, not part of the two counts above. */
    dismissedCandidates: number;
  };
  candidates: DuplicateCandidate[];
}

export interface DuplicateCard {
  id: number;
  displayName: string | null;
  description: string | null;
}

export interface DuplicateGroupMembership {
  groupId: number;
  groupName: string;
  providerId: number | null;
  providerName: string | null;
  fortnoxSynced: boolean;
}

export interface DuplicateAddressGroupMembership {
  groupId: number;
  groupName: string;
  fortnoxSynced: boolean;
}

export interface DuplicateOrganisationAddress {
  id: number;
  addressName: string | null;
  streetAddress: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  type: string;
  active: boolean;
  groupMemberships: DuplicateAddressGroupMembership[];
}

export interface DuplicateOrganisationDetail {
  id: number;
  identificationNumber: string;
  identificationNumberNormalized: string | null;
  identificationNumberStatus: 'valid' | 'invalid' | 'unknown' | 'missing' | null;
  identificationNumberType: string | null;
  name: string;
  displayName: string | null;
  description: string | null;
  vatNumber: string | null;
  customUiEnabled: boolean | null;
  customUiColor: string | null;
  customUiIcon: string | null;
  keywords: string[] | null;
  keywordsOverride: boolean | null;
  disabled: boolean | null;
  homeScreenEnabled: boolean | null;
  customerCardEnabled: boolean | null;
  descriptionCard: DuplicateCard | null;
  mapCard: DuplicateCard | null;
  addresses: DuplicateOrganisationAddress[];
  groupMemberships: DuplicateGroupMembership[];
  linkCount: number;
  scanCount: number;
  userCount: number;
  fortnoxSynced: boolean;
}

export interface DuplicateCandidateDetail extends DuplicateDismissalState {
  candidateKey: string;
  country: string | null;
  digits: string;
  organisations: DuplicateOrganisationDetail[];
}

/** Organisation fields a Merge can take from another Organisation than the Survivor. */
export type DuplicateMergeField =
  | 'name'
  | 'displayName'
  | 'description'
  | 'vatNumber'
  | 'customUiEnabled'
  | 'customUiColor'
  | 'customUiIcon'
  | 'keywords'
  | 'keywordsOverride'
  | 'disabled'
  | 'homeScreenEnabled'
  | 'customerCardEnabled';

export interface DuplicateMergeRequest {
  survivorId: number;
  /** The candidate's Organisations as shown, the Survivor included. */
  organisationIds: number[];
  /** Which Organisation each field is taken from. A field left out keeps the Survivor's value. */
  fieldSources: Partial<Record<DuplicateMergeField, number>>;
  /** Every Organisation's keywords, each once, instead of one Organisation's. Excludes fieldSources.keywords. */
  mergeKeywords?: boolean;
  /** Null lets the backend keep the Survivor's card, or the only one there is. */
  descriptionCardFrom: number | null;
  mapCardFrom: number | null;
}

export interface DuplicateMergeMembership {
  organisationId: number;
  providerId: number | null;
  providerName: string | null;
  fortnoxSynced: boolean;
}

export interface DuplicateMergeSummary {
  /** Null for a preview. */
  mergeId: number | null;
  moved: {
    addresses: number;
    contacts: number;
    referralLinks: number;
    articles: number;
    articleTemplates: number;
    links: number;
    scans: number;
    users: number;
    categories: number;
  };
  /** Contacts deleted: the same contact as a kept one, or without name, email and phone. */
  droppedContacts: { duplicates: number; empty: number };
  collapsedMemberships: {
    groupId: number;
    groupName: string;
    /** Whether a dropped membership had another Provider than the kept one. */
    providersDiffer: boolean;
    keptProvider: DuplicateMergeMembership;
    droppedProviders: DuplicateMergeMembership[];
  }[];
  droppedCards: { card: 'description' | 'map'; cardId: number; fromOrganisationId: number }[];
  fortnoxSynced: boolean;
}

export type BackfillJobState = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED';

export interface BackfillJobStatus {
  jobId: string;
  state: BackfillJobState;
  jobType?: 'KEYWORDS' | 'ORGNO' | 'REVALIDATE_ORGNO' | null;
  queuedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
}

// ─── Onboarding ───────────────────────────────────────────────────────────────

/** APPLIED and IN_PROGRESS can still be edited by the applicant; the rest are read-only for them. */
export type OnboardingStatus = 'APPLIED' | 'IN_PROGRESS' | 'SUBMITTED' | 'VERIFIED' | 'REJECTED' | 'EXPIRED';

export interface OnboardingContact {
  name: string;
  email?: string | null;
  phone?: string | null;
}

export interface OnboardingCompany {
  name: string;
  orgNumber: string;
  /** ISO-3166-1 alpha-2; only SE for now. */
  country: string;
}

export interface OnboardingUser {
  contacts: OnboardingContact[];
  /** The password itself is never returned. */
  passwordSet: boolean;
}

export interface OnboardingPayment {
  /** Only INVOICE for now; card payment comes later. */
  method: 'INVOICE';
  invoiceEmail: string;
  reference?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
}

/** A chosen service, with name and price as they were when it was chosen. */
export interface OnboardingSelectedService {
  serviceId: string;
  name: string;
  price: number;
}

export interface OnboardingDraft {
  company?: OnboardingCompany | null;
  services?: OnboardingSelectedService[] | null;
  user?: OnboardingUser | null;
  payment?: OnboardingPayment | null;
}

/** A service an applicant can choose during onboarding. Each price tier is its own service. */
export interface Service {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  /** Inactive services are hidden from the stepper but kept on existing applications. */
  active: boolean;
}

export interface ServiceWriteView {
  name: string;
  description?: string | null;
  price: number;
  active?: boolean;
}

/** What the applicant sees in the stepper. */
export interface OnboardingSession {
  email: string;
  status: OnboardingStatus;
  expiresAt: string;
  draft: OnboardingDraft;
}

/** Step 2 as sent by the applicant. Leave `password` out to keep the saved one. */
export interface OnboardingUserRequest {
  password?: string;
  contacts: OnboardingContact[];
}

export type OnboardingWarning = {
  code: 'ORG_NUMBER_EXISTS' | 'EMAIL_EXISTS';
  message: string;
  organisationIds: number[];
  userId?: number | null;
};

export interface OnboardingApplicationListItem {
  id: string;
  email: string;
  status: OnboardingStatus;
  companyName?: string | null;
  orgNumber?: string | null;
  createdAt: string;
  submittedAt?: string | null;
  reviewedAt?: string | null;
}

export type OnboardingApplicationSortBy =
  | 'createdAt'
  | 'submittedAt'
  | 'reviewedAt'
  | 'email'
  | 'status'
  | 'companyName'
  | 'orgNumber';

/** All optional. The backend defaults: limit 100, offset 0, all statuses, newest first. */
export interface OnboardingApplicationListParams {
  limit?: number;
  offset?: number;
  /** Case-insensitive search in email, company name and org number. */
  q?: string;
  status?: OnboardingStatus;
  sortBy?: OnboardingApplicationSortBy;
  sortDir?: 'asc' | 'desc';
}

/** A freshly created invitation link; it isn't stored, so it can only be shown right after creating it. */
export interface OnboardingInvitationLink {
  link: string;
  expiresAt: string;
}

export interface OnboardingApplication extends OnboardingApplicationListItem {
  draft: OnboardingDraft;
  /** Verify is refused while any of these applies. */
  warnings: OnboardingWarning[];
  /** Set once verified. */
  organisationId?: number | null;
  userId?: number | null;
  reviewedByUserId?: number | null;
  rejectionReason?: string | null;
}
